// Photo capture mailbox.
//
// The shutter button is DOM and the pixels live inside the r3f canvas, so the
// button parks a request here and PhotoCapture (mounted in the scene) picks it
// up on the next frame. Same mailbox pattern as touch input and the detection
// overlay: no store write, so pressing the shutter can't re-render the world.

export const photoRequest = { pending: null }

// Instagram-shaped crops. `null` means whatever the window already is.
export const PHOTO_ASPECTS = [
  { id: 'portrait', label: '4:5', ratio: 4 / 5 },
  { id: 'story', label: '9:16', ratio: 9 / 16 },
  { id: 'square', label: '1:1', ratio: 1 },
  { id: 'full', label: 'full', ratio: null },
]

/**
 * Ask the scene for a PNG of the current view, cropped to `ratio`.
 * Resolves with a data URL, or null if the capture came back empty.
 */
export function requestPhoto(ratio) {
  return new Promise((resolve) => {
    photoRequest.pending = { ratio, resolve }
  })
}

/**
 * Centre-crop the live canvas to an aspect and return a PNG data URL.
 *
 * Two WebGPU quirks shape this, both found the hard way:
 *
 * 1. `canvas.toDataURL()` reads the WebGPU canvas correctly, but
 *    `drawImage(webgpuCanvas, ...)` into a 2D context returns a fully
 *    transparent image. So the crop goes via an Image decoded from the full
 *    data URL rather than straight off the canvas.
 * 2. Don't re-render before reading. This runs inside the frame loop, and
 *    asking the renderer for another frame from in there leaves the promise
 *    hanging forever. The just-presented frame is still readable, which is
 *    all we need.
 */
export async function captureCanvas(gl, ratio) {
  const src = gl?.domElement
  const sw = src?.width
  const sh = src?.height
  if (!sw || !sh) return null

  let full
  try {
    full = src.toDataURL('image/png')
  } catch {
    return null
  }
  if (!ratio) return full

  // Largest rectangle of this ratio that fits, centred.
  let cw = sw
  let ch = sh
  if (sw / sh > ratio) cw = Math.round(sh * ratio)
  else ch = Math.round(sw / ratio)
  const ox = Math.floor((sw - cw) / 2)
  const oy = Math.floor((sh - ch) / 2)

  const img = new Image()
  img.src = full
  try {
    await img.decode()
  } catch {
    return full // an uncropped shot beats no shot
  }

  const out = document.createElement('canvas')
  out.width = cw
  out.height = ch
  const ctx = out.getContext('2d')
  if (!ctx) return full
  ctx.drawImage(img, ox, oy, cw, ch, 0, 0, cw, ch)
  return out.toDataURL('image/png')
}

/** Trigger a browser download for a data URL. */
export function downloadPhoto(dataUrl, name) {
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
}
